import { Link } from "react-router-dom";
import { Mail, Instagram, Linkedin, Youtube, Facebook } from "lucide-react";
import { openConsent } from "@/lib/cookies";
import { getLegalNav } from "@/lib/legalContent";
import { useT, useLang } from "@/lib/i18n";
import DownloadCatalogButton from "@/components/DownloadCatalogButton";
import NewsletterSignup from "@/components/NewsletterSignup";

const LOGO = "https://customer-assets.emergentagent.com/job_e33c4909-1cdb-40b6-bbfc-eef1964c596a/artifacts/qhav103f_geoVINUM_logo_RVB_300ppi_transparent.png";

const PinterestIcon = ({ size = 16, ...props }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 0C5.4 0 0 5.4 0 12c0 5.06 3.13 9.39 7.57 11.12-.1-.94-.2-2.4.04-3.44.22-.93 1.4-5.94 1.4-5.94s-.36-.72-.36-1.78c0-1.66.97-2.91 2.17-2.91 1.03 0 1.52.77 1.52 1.69 0 1.03-.66 2.58-1 4.01-.28 1.21.6 2.19 1.79 2.19 2.15 0 3.81-2.27 3.81-5.54 0-2.9-2.09-4.93-5.07-4.93-3.45 0-5.48 2.59-5.48 5.27 0 1.04.4 2.16.9 2.77.1.12.11.22.08.34l-.34 1.36c-.05.22-.18.27-.4.16-1.5-.7-2.43-2.89-2.43-4.65 0-3.78 2.75-7.26 7.92-7.26 4.16 0 7.39 2.97 7.39 6.93 0 4.13-2.6 7.46-6.22 7.46-1.21 0-2.36-.63-2.75-1.38l-.75 2.85c-.27 1.04-1 2.35-1.49 3.15.71.22 1.46.34 2.24.34 6.6 0 12-5.4 12-12S18.6 0 12 0z"/>
  </svg>
);

const PRODUCT_LINKS = [
  { to: "/shop", label: "Maps" },
  { to: "/app", label: "App" },
  { href: "https://iframe.geovinum.app/", label: "Interactive", external: true },
];

// Social URLs — switched by current UI language.
// FR audience → French accounts (geoVINUMbyEtOH, geoVINUM France, etc.)
// All other languages → English accounts (geoVINUM | Wine Maps & Learning, etc.)
// Pinterest, LinkedIn (company), YouTube channel and X handle are universal.
const SOCIALS_FR = [
  { href: "https://www.facebook.com/geoVINUMbyEtOH", label: "Facebook", Icon: Facebook },
  { href: "https://www.instagram.com/geovinummaps", label: "Instagram", Icon: Instagram },
  { href: "https://www.linkedin.com/company/geovinum", label: "LinkedIn", Icon: Linkedin },
  { href: "https://www.pinterest.com/geovinummaps", label: "Pinterest", Icon: PinterestIcon },
  { href: "https://www.youtube.com/channel/UCrIZDOeOOVZHVtEgtLe2CVg", label: "YouTube", Icon: Youtube },
  { href: "https://x.com/geoVINUM", label: "X", Icon: (props) => <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
];

const SOCIALS_EN = [
  { href: "https://www.facebook.com/geovinum", label: "Facebook", Icon: Facebook },
  { href: "https://www.instagram.com/geovinum.io", label: "Instagram", Icon: Instagram },
  { href: "https://www.linkedin.com/company/geovinum", label: "LinkedIn", Icon: Linkedin },
  { href: "https://www.pinterest.com/geovinummaps", label: "Pinterest", Icon: PinterestIcon },
  { href: "https://www.youtube.com/channel/UCrIZDOeOOVZHVtEgtLe2CVg", label: "YouTube", Icon: Youtube },
  { href: "https://x.com/geoVINUM", label: "X", Icon: (props) => <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
];

const PILLAR_LABELS = {
  fr: { glossaire: "Glossaire du vin", cartographie: "Cartographie viticole", geographie: "Géographie du vin", appellations: "Appellations viticoles", terroirs: "Terroirs viticoles" },
  en: { glossaire: "Wine glossary", cartographie: "Wine cartography", geographie: "Wine geography", appellations: "Wine appellations", terroirs: "Wine terroirs" },
  es: { glossaire: "Glosario del vino", cartographie: "Cartografía vitivinícola", geographie: "Geografía del vino", appellations: "Denominaciones vitivinícolas", terroirs: "Terruños vitivinícolas" },
  pt: { glossaire: "Glossário do vinho", cartographie: "Cartografia vitícola", geographie: "Geografia do vinho", appellations: "Denominações vitícolas", terroirs: "Terroirs vitícolas" },
};

export const Footer = () => {
  const t = useT();
  const lang = useLang();
  const legalNav = getLegalNav(lang);
  const pl = PILLAR_LABELS[lang] || PILLAR_LABELS.fr;
  return (
    <footer className="bg-[color:var(--gv-ink)] text-white pt-16 pb-8" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-12">
          <div className="col-span-2">
            <img src={LOGO} alt="geoVINUM" className="h-9 w-auto mb-4 brightness-0 invert" />
            <p className="text-[13px] text-white/60 max-w-xs leading-relaxed">{t("home.hero.sub_v2")}</p>
          </div>

          <div>
            <div className="text-[12px] uppercase tracking-widest text-white/45 mb-4">{t("footer.heading.products")}</div>
            <ul className="space-y-2.5 text-[13.5px]">
              {PRODUCT_LINKS.map((p) => (
                <li key={p.label}>
                  {p.external ? (
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-white/75 hover:text-[color:var(--gv-pink)] inline-flex items-center gap-2"
                      data-testid={`footer-product-${p.label.toLowerCase()}`}
                    >
                      {p.label}
                    </a>
                  ) : (
                    <Link to={p.to} className="text-white/75 hover:text-[color:var(--gv-pink)] inline-flex items-center gap-2" data-testid={`footer-product-${p.label.toLowerCase()}`}>
                      {p.label}
                      {p.soon && <span className="text-[10px] uppercase tracking-widest text-white/35">{t("product.status.coming_soon")}</span>}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-[12px] uppercase tracking-widest text-white/45 mb-4">{t("footer.heading.resources")}</div>
            <ul className="space-y-2.5 text-[13.5px]">
              <li><Link to="/blog" className="text-white/75 hover:text-[color:var(--gv-pink)]">Blog</Link></li>
              <li><Link to="/glossaire" className="text-white/75 hover:text-[color:var(--gv-pink)]" data-testid="footer-link-glossaire">{pl.glossaire}</Link></li>
              <li><Link to="/cartographie-viticole" className="text-white/75 hover:text-[color:var(--gv-pink)]" data-testid="footer-link-cartographie">{pl.cartographie}</Link></li>
              <li><Link to="/geographie-du-vin" className="text-white/75 hover:text-[color:var(--gv-pink)]" data-testid="footer-link-geographie">{pl.geographie}</Link></li>
              <li><Link to="/appellations-viticoles" className="text-white/75 hover:text-[color:var(--gv-pink)]" data-testid="footer-link-appellations">{pl.appellations}</Link></li>
              <li><Link to="/terroirs-viticoles" className="text-white/75 hover:text-[color:var(--gv-pink)]" data-testid="footer-link-terroirs">{pl.terroirs}</Link></li>
              <li><DownloadCatalogButton variant="ghost" testId="footer-catalog-download" className="!text-white/75 hover:!text-[color:var(--gv-pink)]" /></li>
              <li><Link to="/contact" className="text-white/75 hover:text-[color:var(--gv-pink)]" data-testid="footer-link-contact">{t("footer.link.contact")}</Link></li>
              <li><a href="mailto:hello@geovinum.io" className="text-white/75 hover:text-[color:var(--gv-pink)]">{t("footer.link.support")}</a></li>
            </ul>
            <div className="text-[12px] uppercase tracking-widest text-white/45 mt-7 mb-4">{t("footer.link.newsletter")}</div>
            <NewsletterSignup source="footer" variant="footer" />
          </div>

          <div>
            <div className="text-[12px] uppercase tracking-widest text-white/45 mb-4">{t("footer.heading.company")}</div>
            <ul className="space-y-2.5 text-[13.5px]">
              <li><Link to="/about" className="text-white/75 hover:text-[color:var(--gv-pink)]" data-testid="footer-link-about">{t("footer.link.about")}</Link></li>
              <li><Link to="/faq" className="text-white/75 hover:text-[color:var(--gv-pink)]" data-testid="footer-link-faq">FAQ</Link></li>
              <li><Link to="/contact" className="text-white/75 hover:text-[color:var(--gv-pink)]">{t("footer.link.contact")}</Link></li>
            </ul>
            <div className="text-[12px] uppercase tracking-widest text-white/45 mt-7 mb-4">{t("footer.heading.social")}</div>
            <div className="flex flex-wrap gap-3">
              {(lang === "fr" ? SOCIALS_FR : SOCIALS_EN).map(({ href, label, Icon }) => (
                <a key={label} href={href} target="_blank" rel="noreferrer" aria-label={label} title={label} className="w-9 h-9 rounded-[5px] border border-white/15 flex items-center justify-center text-white/80 hover:text-[color:var(--gv-pink)] hover:border-[color:var(--gv-pink)] transition-colors" data-testid={`footer-social-${label.toLowerCase()}`}>
                  <Icon size={16} />
                </a>
              ))}
              <a href="mailto:hello@geovinum.io" aria-label="Email" title="Email" className="w-9 h-9 rounded-[5px] border border-white/15 flex items-center justify-center text-white/80 hover:text-[color:var(--gv-pink)] hover:border-[color:var(--gv-pink)] transition-colors">
                <Mail size={16} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-white/45">
          <div>© {new Date().getFullYear()} geoVINUM. {t("footer.rights") || "All rights reserved."}</div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {legalNav.map((l) => (
              <Link key={l.slug} to={`/legal/${l.slug}`} className="hover:text-[color:var(--gv-pink)]">{l.label}</Link>
            ))}
            <button onClick={openConsent} className="hover:text-[color:var(--gv-pink)]" data-testid="footer-cookie-settings">{t("cookies.manage")}</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
